/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { CustomToggleButton, ToggleMenu } from '@opengeoweb/shared';
import { Share } from '@opengeoweb/theme';
import type { WarningType } from '../../store/warningsForm/types';
import { useWarningsTranslation } from '../../utils/i18n';
import { getShareDrawingMenuItems } from '../AreaDrawingManager/shareDrawing';

export interface ShareDrawingButtonProps {
  onClickShare: (type: WarningType) => void;
}

export const ShareDrawingButton: React.FC<ShareDrawingButtonProps> = ({
  onClickShare,
}) => {
  const { t } = useWarningsTranslation();

  const menuItems = React.useMemo(
    () => getShareDrawingMenuItems(t, onClickShare),
    [t, onClickShare],
  );

  return (
    <ToggleMenu
      menuItems={menuItems}
      menuPosition="top"
      menuTitle={t('area-drawing-manager-share-menu')}
      renderButton={({ onClick, isOpen }): React.ReactNode => (
        <CustomToggleButton
          data-testid="shareDrawingFormButton"
          disableRipple
          onClick={(event: React.MouseEvent<HTMLElement>): void => {
            onClick(event);
          }}
          selected={isOpen}
          size="small"
          sx={{
            width: '100%!important',
            height: '40px!important',
            textTransform: 'none!important',
            gap: 0.5,
          }}
          value="share"
          variant="boxed"
        >
          <Share /> {t('area-drawing-toolbox-button-share')}
        </CustomToggleButton>
      )}
    />
  );
};
